import {
  renderIslandName,
  renderLetterBody,
  sealKey,
  type Content,
  type Fragment,
  type FormatArgs,
  type IslandNameStructure,
  type LetterBody,
  type ManuscriptSpan,
  type Relic,
  type ReputationTier,
  type api,
} from '@guild/core'

import type { ApiFailure } from './api.ts'

/**
 * Ids into words.
 *
 * The API carries ids and the client renders them (Q12.2): an island name is a
 * structure, a log line is an event and a seed, a code name, a relic and a
 * fragment are catalogue ids, and a letter body is ids all the way down. This
 * module is the only place in the app where any of that becomes a sentence,
 * and every function here goes through the core loaders in the reader's
 * locale. No screen composes a string of its own.
 */

/** Anything that can read content in a locale. The app itself is one. */
export type Reader = { readonly content: Content; readonly locale: string }

/**
 * A long content string as its paragraphs (#94).
 *
 * `welcome`, `codeOfConduct` and the circulars are written with blank lines
 * between their paragraphs, and a single `<p>` would run them together into a
 * wall. Splitting is presentation and not composition: nothing here joins two
 * strings, it only stops pretending four paragraphs are one.
 */
export const paragraphs = (text: string): string[] =>
  text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '')

/** A `ui` string, formatted. A key the locale lacks renders as the key. */
export const t = (reader: Reader, key: string, args?: FormatArgs): string =>
  reader.content.t(reader.locale, key, args)

export const islandName = (reader: Reader, structure: IslandNameStructure): string =>
  renderIslandName(structure, reader.content.islandNames(reader.locale))

/** "Day 4", or the line for an island older than the epoch (Q1.3). */
export const dayLabel = (reader: Reader, dayNumber: number | null): string =>
  dayNumber === null ? t(reader, 'dayUnnumbered') : t(reader, 'dayNumber', { n: dayNumber })

/**
 * "the 4th morning", beside the day number.
 *
 * The ordinal is the locale's own, made by ICU `selectordinal` from the number
 * (Q18.2) -- a list of English ordinal words in this file would be exactly the
 * literal a component may not hold. Null for an unnumbered island, which has no
 * morning to count.
 */
export const dayOrdinal = (reader: Reader, dayNumber: number | null): string | null =>
  dayNumber === null ? null : t(reader, 'dayOrdinal', { n: dayNumber })

/** `lines[seed % lines.length]` in the reader's locale, as the server intends. */
export const logLine = (reader: Reader, line: api.ApiLogLine | null | undefined): string | null =>
  line === null || line === undefined
    ? null
    : (reader.content.logLine(reader.locale, line.event, line.seed) ?? null)

/** A code name's own words, with no punctuation of ours around them. */
export const codeNameText = (reader: Reader, id: string): string =>
  reader.content.codeName(reader.locale, id)?.text ?? id

/** How a code name is signed: `— {codeName}`, from content, never from a `+`. */
export const codeNameLine = (reader: Reader, id: string): string =>
  t(reader, 'postSealedFrom', { codeName: codeNameText(reader, id) })

export const relic = (reader: Reader, id: string): Relic | undefined =>
  reader.content.relic(reader.locale, id)

export const relicName = (reader: Reader, id: string): string => relic(reader, id)?.name ?? id

export const fragment = (reader: Reader, id: string): Fragment | undefined =>
  reader.content.fragment(reader.locale, id)

export const fragmentText = (reader: Reader, id: string): string => fragment(reader, id)?.text ?? id

export const manuscriptSpans = (reader: Reader): readonly ManuscriptSpan[] =>
  reader.content.manuscriptSpans(reader.locale)

/**
 * A letter body as prose, in the **reader's** locale (Q12.3).
 *
 * A body that the reader's grammar cannot render is a content mismatch rather
 * than a player's problem: the renderer throws, and the screen shows nothing
 * where the prose would be instead of an error page.
 */
export function letterProse(reader: Reader, body: LetterBody): string {
  try {
    return renderLetterBody(body, reader.content.grammar(reader.locale))
  } catch {
    return ''
  }
}

/** The tier word's sentence: `sealTrusted` and its three siblings (Q7.4). */
export const sealText = (reader: Reader, tier: ReputationTier): string => t(reader, sealKey(tier))

/**
 * The direction words, by their grammar ids.
 *
 * The four-way control's buttons are arrows on screen and these underneath, so
 * a screen reader says "north" rather than "up arrow" -- and says it in the
 * reader's language, because the letter grammar already has the word.
 */
export const directionLabel = (reader: Reader, id: string): string =>
  reader.content.grammar(reader.locale).slots.direction.find((slot) => slot.id === id)?.text ?? id

/**
 * The `ui` key an API error code is said with.
 *
 * The server sends codes and never sentences, so this is the whole of the
 * translation. A code with no key of its own falls to `errorGeneric`, which is
 * why an unknown failure still speaks in the guild's voice.
 */
const ERROR_KEYS: Readonly<Record<string, string>> = {
  offline: 'errorOffline',
  'rate-limited': 'errorRateLimited',
  'username-shape': 'authUsernameRule',
  'username-taken': 'authUsernameTaken',
  'username-reserved': 'authUsernameReserved',
  'login-failed': 'authLoginFailed',
  'already-sent-today': 'letterOneADay',
  'not-todays-island': 'draftNotTodaysIsland',
  'fragment-not-held': 'draftFragmentNotHeld',
  'no-draft': 'draftNone',
  // The sinking day has not begun until the boat has put in (Q21.1). The
  // refusal carries no log event, because nothing happened on the ground; the
  // line is the one the screen was already asking with.
  'choose-a-shore': 'returnChooseShore',
}

export const errorKey = (code: string): string => ERROR_KEYS[code] ?? 'errorGeneric'

/** What to say about a failure. A refusal with a log event says that instead. */
export function errorText(reader: Reader, failure: ApiFailure): string {
  const line = logLine(reader, failure.log)
  return line ?? t(reader, errorKey(failure.error))
}
